import React, { useState, useMemo } from 'react';
import {
  extractVersionSceneOrder,
  formatVersionTimestamp,
  getSceneGenerations,
  type VideoProject,
  type VideoVersion,
} from './videoStudioTypes';

interface VideoSourcePreviewProps {
  project: VideoProject;
  onSelectOfficialVersion?: (versionId: string, videoUrl: string) => void;
  onSelectSceneGeneration?: (sceneOrder: number, videoUrl: string, durationSeconds?: number | null) => void;
}

type PreviewScope = 'official' | 'all' | number;

export const VideoSourcePreview: React.FC<VideoSourcePreviewProps> = ({
  project,
  onSelectOfficialVersion,
  onSelectSceneGeneration,
}) => {
  const versions = project.versions || [];
  const scenes = project.scenes || [];
  const selectedVersion =
    versions.find((v) => v.id === project.selected_version_id || Boolean(v.is_selected)) ||
    versions[0] ||
    null;

  const [scope, setScope] = useState<PreviewScope>('official');
  const [customVideoUrl, setCustomVideoUrl] = useState<string | null>(null);

  const sceneGroups = useMemo(() => {
    return scenes.map((sc) => ({
      sceneOrder: sc.scene_order,
      activeSceneUrl: sc.video_url || null,
      generations: getSceneGenerations(versions, sc.scene_order, sc.video_url, sc.duration_seconds),
    }));
  }, [scenes, versions]);

  const currentScopeOptions = useMemo(() => {
    if (typeof scope === 'number') {
      const found = sceneGroups.find((g) => g.sceneOrder === scope);
      return found?.generations || [];
    }
    if (scope === 'all') {
      return versions.map((v, idx) => ({
        ...v,
        generationNumber: versions.length - idx,
        sceneOrder: extractVersionSceneOrder(v),
      }));
    }
    // scope === 'official': prioriza vídeos consolidados + versão oficial selecionada
    const stitchedOrOfficial = versions.filter(
      (v) => v.source_type === 'scenes_stitched' || v.id === selectedVersion?.id || Boolean(v.is_selected)
    );
    const list = stitchedOrOfficial.length > 0 ? stitchedOrOfficial : versions.slice(0, 5);
    return list.map((v, idx) => ({
      ...v,
      generationNumber: list.length - idx,
      sceneOrder: extractVersionSceneOrder(v),
    }));
  }, [scope, sceneGroups, versions, selectedVersion]);

  const activePreviewVersion: (VideoVersion & { sceneOrder?: number | null; generationNumber?: number }) | null = useMemo(() => {
    if (customVideoUrl) {
      const fromScope = currentScopeOptions.find((v) => v.video_url === customVideoUrl);
      if (fromScope) return fromScope;
      const fromAll = versions.find((v) => v.video_url === customVideoUrl);
      if (fromAll) return { ...fromAll, sceneOrder: extractVersionSceneOrder(fromAll) };
    }
    if (typeof scope === 'number') {
      const group = sceneGroups.find((g) => g.sceneOrder === scope);
      const activeInScene = group?.generations.find((g) => g.video_url === group.activeSceneUrl);
      return activeInScene || group?.generations[0] || selectedVersion;
    }
    return selectedVersion || currentScopeOptions[0] || null;
  }, [customVideoUrl, currentScopeOptions, versions, scope, sceneGroups, selectedVersion]);

  const previewUrl = activePreviewVersion?.video_url || selectedVersion?.video_url || null;
  const previewSceneOrder =
    typeof scope === 'number' ? scope : extractVersionSceneOrder(activePreviewVersion);
  const matchingScene = previewSceneOrder
    ? scenes.find((s) => s.scene_order === previewSceneOrder)
    : null;
  const isCurrentSceneActiveTake = Boolean(
    matchingScene && previewUrl && matchingScene.video_url === previewUrl
  );
  const isOfficialSelected = Boolean(
    activePreviewVersion &&
      (activePreviewVersion.id === project.selected_version_id || Boolean(activePreviewVersion.is_selected))
  );

  const visualRef = project.source_asset_url || project.references?.find((r) => Boolean(r.asset_url))?.asset_url;
  const visualRefLabel = project.source_asset_url
    ? 'Arte da Campanha'
    : project.references?.find((r) => Boolean(r.asset_url))?.label || 'Referência Visual';

  return (
    <div className="vstudio-card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.65rem' }}>
        <h4 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 700 }}>Preview & Gerações</h4>
        <span className={`vstudio-badge ${previewUrl ? 'vstudio-badge-success' : visualRef ? 'vstudio-badge-primary' : 'vstudio-badge-warning'}`}>
          {previewUrl ? 'Vídeo Pronto' : visualRef ? 'Referência Ativa' : 'Aguardando Produção'}
        </span>
      </div>

      {/* Filtro rápido por Cena ou Consolidado */}
      {versions.length > 0 && (
        <div style={{ marginBottom: '0.65rem' }}>
          <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap', marginBottom: '0.45rem' }}>
            <button
              type="button"
              onClick={() => {
                setScope('official');
                setCustomVideoUrl(selectedVersion?.video_url || null);
              }}
              style={{
                background: scope === 'official' ? '#2563eb' : 'rgba(30, 41, 59, 0.85)',
                color: scope === 'official' ? '#fff' : '#cbd5e1',
                border: '1px solid rgba(148, 163, 184, 0.25)',
                borderRadius: '0.35rem',
                padding: '0.2rem 0.5rem',
                fontSize: '0.72rem',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              ⭐ Principal
            </button>
            {sceneGroups.map((g) => (
              <button
                key={g.sceneOrder}
                type="button"
                onClick={() => {
                  setScope(g.sceneOrder);
                  setCustomVideoUrl(g.activeSceneUrl || g.generations[0]?.video_url || null);
                }}
                style={{
                  background: scope === g.sceneOrder ? '#4f46e5' : 'rgba(30, 41, 59, 0.85)',
                  color: scope === g.sceneOrder ? '#fff' : '#cbd5e1',
                  border: '1px solid rgba(148, 163, 184, 0.25)',
                  borderRadius: '0.35rem',
                  padding: '0.2rem 0.5rem',
                  fontSize: '0.72rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Cena #{g.sceneOrder} ({g.generations.length})
              </button>
            ))}
            <button
              type="button"
              onClick={() => {
                setScope('all');
                setCustomVideoUrl(previewUrl);
              }}
              style={{
                background: scope === 'all' ? '#0f766e' : 'rgba(30, 41, 59, 0.85)',
                color: scope === 'all' ? '#fff' : '#cbd5e1',
                border: '1px solid rgba(148, 163, 184, 0.25)',
                borderRadius: '0.35rem',
                padding: '0.2rem 0.5rem',
                fontSize: '0.72rem',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Todas ({versions.length})
            </button>
          </div>

          {/* Seletor das gerações disponíveis no filtro atual */}
          {currentScopeOptions.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
              <select
                className="vstudio-select"
                style={{ fontSize: '0.75rem', padding: '0.3rem 0.5rem' }}
                value={previewUrl || ''}
                onChange={(e) => setCustomVideoUrl(e.target.value || null)}
              >
                {currentScopeOptions.map((opt) => {
                  const ts = formatVersionTimestamp(opt.created_at);
                  const scLabel =
                    opt.source_type === 'scenes_stitched'
                      ? '🎞️ Vídeo Consolidado'
                      : opt.sceneOrder
                      ? `Cena #${opt.sceneOrder} • Take v${opt.generationNumber}`
                      : `Versão v${opt.generationNumber}`;
                  const durLabel = opt.duration_seconds ? ` (${opt.duration_seconds}s)` : '';
                  const activeTag =
                    opt.sceneOrder && scenes.find((s) => s.scene_order === opt.sceneOrder)?.video_url === opt.video_url
                      ? ' [✓ Em uso na Cena]'
                      : opt.id === project.selected_version_id || Boolean(opt.is_selected)
                      ? ' [⭐ Principal]'
                      : '';
                  return (
                    <option key={opt.id} value={opt.video_url}>
                      {scLabel}
                      {durLabel}
                      {ts ? ` — ${ts}` : ''}
                      {activeTag}
                    </option>
                  );
                })}
              </select>
            </div>
          )}
        </div>
      )}

      <div style={{
        position: 'relative',
        width: '100%',
        borderRadius: '0.5rem',
        overflow: 'hidden',
        background: '#090d16',
        aspectRatio: project.aspect_ratio === '9:16' ? '9/16' : project.aspect_ratio === '16:9' ? '16/9' : '1/1',
        maxHeight: '380px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto',
        border: '1px solid rgba(255, 255, 255, 0.08)'
      }}>
        {previewUrl ? (
          <video
            key={previewUrl}
            src={previewUrl}
            controls
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            poster={activePreviewVersion?.thumbnail_url || visualRef || undefined}
          />
        ) : visualRef ? (
          <div style={{ position: 'relative', width: '100%', height: '100%' }}>
            <img
              src={visualRef}
              alt={visualRefLabel}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
            <div style={{
              position: 'absolute',
              bottom: '0.5rem',
              left: '0.5rem',
              background: 'rgba(15, 23, 42, 0.85)',
              color: '#f8fafc',
              fontSize: '0.72rem',
              padding: '0.2rem 0.5rem',
              borderRadius: '0.35rem',
              backdropFilter: 'blur(4px)',
              fontWeight: 600,
            }}>
              📷 {visualRefLabel}
            </div>
          </div>
        ) : (
          <div style={{ color: '#94a3b8', fontSize: '0.85rem', textAlign: 'center', padding: '1rem' }}>
            Nenhuma imagem ou vídeo vinculado
          </div>
        )}
      </div>

      {/* Ações rápidas para a geração atualmente em preview */}
      {previewUrl && (
        <div style={{ marginTop: '0.6rem', display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
          {previewSceneOrder && onSelectSceneGeneration && !isCurrentSceneActiveTake && (
            <button
              type="button"
              className="vstudio-btn vstudio-btn-primary"
              style={{ width: '100%', padding: '0.35rem 0.6rem', fontSize: '0.75rem', fontWeight: 700 }}
              onClick={() =>
                onSelectSceneGeneration(
                  previewSceneOrder,
                  previewUrl,
                  activePreviewVersion?.duration_seconds
                )
              }
            >
              ✓ Usar esta geração na Cena #{previewSceneOrder}
            </button>
          )}
          {activePreviewVersion &&
            !activePreviewVersion.id.startsWith('scene_') &&
            !isOfficialSelected &&
            onSelectOfficialVersion && (
              <button
                type="button"
                className="vstudio-btn vstudio-btn-secondary"
                style={{ width: '100%', padding: '0.35rem 0.6rem', fontSize: '0.74rem', fontWeight: 600 }}
                onClick={() => onSelectOfficialVersion(activePreviewVersion.id, previewUrl)}
              >
                ⭐ Definir como Vídeo Principal
              </button>
            )}
        </div>
      )}

      <div style={{ marginTop: '0.85rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
        <span className="vstudio-badge vstudio-badge-secondary">
          📐 Formato: {project.aspect_ratio}
        </span>
        <span className="vstudio-badge vstudio-badge-secondary">
          ⏱ Alvo: {project.target_duration_seconds}s
        </span>
        <span className="vstudio-badge vstudio-badge-primary">
          🎬 Cenas: {project.scenes?.length || 0}
        </span>
        <span className="vstudio-badge vstudio-badge-secondary">
          🏷 Rev: v{project.revision_number}
        </span>
      </div>
    </div>
  );
};
